import SearchspringListingBase from "@theme/searchspring-base";
import { localizeUrl } from '@theme/localize-url';
import { formatMoney, fetchPriceData } from '@theme/theme-currency';

export default class CrossSellComponent extends SearchspringListingBase {
  #pageLoadId;

  constructor() {
    super();
    this.#pageLoadId = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
  }

  async connectedCallback() {
    this.productContainer = this.querySelector('.js-product-cross-sell');
    this.template = document.getElementById('product-accessory-template');
    this.siteId = this.dataset.siteId?.trim();
    this.searchTrendingTag = this.dataset.profileTag || 'cross-sell';
    this.productId = this.dataset.productId;

    if (!this.productContainer || !this.template || !this.siteId) return;

    await this.renderCrossSell();
  }

  async apiRequest() {
    const baseUrl = `https://${this.siteId}.a.searchspring.io`;
    const params = new URLSearchParams({
      tags: this.searchTrendingTag,
      products: this.productId,
      limits: this.dataset.limit || '3',
      userId: this.#getCookie('ssUserId'),
      sessionId: this.#getCookie('ssSessionId'),
      pageLoadId: this.#pageLoadId
    });

    const url = `${baseUrl}/boost/${this.siteId}/recommend?${params.toString()}`;

    try {
      const response = await fetch(url, { headers: { accept: 'application/json' } });
      if (!response.ok) throw new Error(`API Error: ${response.status}`);
      const json = await response.json();
      return json[0]?.results || [];
    } catch (error) {
      console.error("Cross-sell Boost Error:", error);
      return [];
    }
  }

  async renderCrossSell() {
    const results = await this.apiRequest();
    if (!results.length) {
      this.classList.add('hidden');
      return;
    }

    const variantGids = results.map(rawProduct => {
      const initialVariant = rawProduct.variants?.data?.[0]?.mappings?.core || rawProduct.mappings?.core;
      return `gid://shopify/ProductVariant/${initialVariant.uid || initialVariant.variant_id}`;
    });

    let priceMap = new Map();
    try {
      const { nodes } = await fetchPriceData(variantGids, {
        GRAPHQL_URL: this.GRAPHQL_URL,
        STOREFRONT_TOKEN: this.STOREFRONT_TOKEN
      });
      nodes.forEach(node => node?.id && priceMap.set(node.id.split('/').pop(), node));
    } catch (e) {
      console.warn("Initial live price fetch failed", e);
    }

    this.productContainer.innerHTML = '';

    results.forEach((rawProduct) => {
      const initialVariant = rawProduct.variants?.data?.[0]?.mappings?.core || rawProduct.mappings.core;
      const initialVId = initialVariant.uid || initialVariant.variant_id;
      const liveNode = priceMap.get(String(initialVId));

      const html = this.prepareTemplate(rawProduct, liveNode);
      this.productContainer.insertAdjacentHTML('beforeend', html);

      const lastCard = this.productContainer.lastElementChild;
      this.#bindOptionEvents(lastCard, rawProduct);
    });
  }

  prepareTemplate(rawProduct, liveNode) {
    const product = rawProduct.mappings.core;
    const initialVariant = rawProduct.variants?.data?.[0]?.mappings?.core || product;

    const price = liveNode ? liveNode.price.amount : initialVariant.price;
    const msrp = liveNode?.compareAtPrice ? liveNode.compareAtPrice.amount : (initialVariant.msrp || 0);
    const initialVId = initialVariant.uid || initialVariant.variant_id;

    const optionsMarkup = this.#generateMultiOptionMarkup(rawProduct);
    const activeImageUrl = initialVariant.imageUrl || product.imageUrl;

    return this.template.innerHTML
      .replace(/\${productId}/g, product.uid || rawProduct.id)
      .replace(/\${variantId}/g, initialVId)
      .replace(/\${productUrl}/g, localizeUrl(initialVariant.url || product.url))
      .replace(/\${imageUrl}/g, activeImageUrl)
      .replace(/\${mainImageSrcset}/g, this.generateSrcset(activeImageUrl))
      .replace(/\${productTitle}/g, product.name)
      .replace(/\${priceMarkup}/g, this.#generatePriceMarkup(price, msrp))
      .replace(/\${optionsMarkup}/g, optionsMarkup);
  }

  #generateMultiOptionMarkup(rawProduct) {
    const optionConfig = rawProduct.variants?.optionConfig;
    if (!optionConfig) return '';

    let swatches = [];
    try {
      swatches = JSON.parse(rawProduct.attributes.ss_swatches || '[]');
    } catch (e) { swatches = []; }

    return Object.keys(optionConfig).map(optionName => {
      const isColor = optionName.toLowerCase() === 'color';
      const uniqueValues = [...new Set(rawProduct.variants.data.map(v => v.options[optionName]?.value))];
      const selectedValue = rawProduct.variants.data[0].options[optionName]?.value;

      const items = uniqueValues.map((val) => {
        const isActive = val === selectedValue ? 'is-active' : '';
        let style = '';
        let buttonClass = isColor ? 'product-card-swatch' : 'variant-option-button';

        if (isColor) {
          const swatch = swatches.find(s => s.color === val);
          style = swatch?.swatch_img ?
            `background-image: url(${swatch.swatch_img}); background-size: cover;` :
            `background-color: ${swatch?.swatch_hex || '#ccc'};`;
        }

        return `
          <button type="button"
            class="${buttonClass} js-variant-option ${isActive}"
            data-option-name="${optionName}"
            data-option-value="${val}"
            style="${style}">
            <strong><span>${val}</span></strong>
          </button>`;
      }).join('');

      return `
        <div class="accessory-option-group" data-option-name="${optionName}">
          <p class="accessory-option-label"><strong>${optionName.charAt(0).toUpperCase() + optionName.slice(1)}:</strong> <span class="js-option-name">${selectedValue}</span></p>
          <div class="product-card-swatches-container">${items}</div>
        </div>`;
    }).join('');
  }

  #bindOptionEvents(card, rawProduct) {
    const buttons = card.querySelectorAll('.js-variant-option');
    const product = rawProduct.mappings.core;

    buttons.forEach(btn => {
      btn.addEventListener('click', async () => {
        if (btn.classList.contains('is-active')) return;

        const group = btn.closest('.accessory-option-group');
        group.querySelectorAll('.js-variant-option').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        group.querySelector('.js-option-name').textContent = btn.dataset.optionValue;

        const currentSelections = Array.from(card.querySelectorAll('.js-variant-option.is-active'))
          .reduce((acc, b) => {
            acc[b.dataset.optionName] = b.dataset.optionValue;
            return acc;
          }, {});

        const match = rawProduct.variants.data.find(v => {
          return Object.keys(currentSelections).every(name => v.options[name].value === currentSelections[name]);
        });

        if (match) {
          const variant = match.mappings.core;
          const mainImg = card.querySelector('.accessory-product__image-main');
          if (mainImg) {
            const newImageUrl = variant.imageUrl || product.imageUrl;
            mainImg.src = newImageUrl;
            mainImg.srcset = this.generateSrcset(newImageUrl);
          }

          card.querySelector('[ref="variantId"]').value = variant.uid;

          // Added updating class to the card holder
          card.classList.add('is-updating');

          try {
            const { nodes } = await fetchPriceData([`gid://shopify/ProductVariant/${variant.uid}`], {
              GRAPHQL_URL: this.GRAPHQL_URL,
              STOREFRONT_TOKEN: this.STOREFRONT_TOKEN
            });
            const node = nodes[0];
            const priceContainer = card.querySelector('.price-container');
            if (priceContainer) {
              priceContainer.innerHTML = node?.price
                ? this.#generatePriceMarkup(node.price.amount, node.compareAtPrice?.amount)
                : this.#generatePriceMarkup(variant.price, variant.msrp);
            }
          } catch (e) {
            const priceContainer = card.querySelector('.price-container');
            if (priceContainer) {
              priceContainer.innerHTML = this.#generatePriceMarkup(variant.price, variant.msrp);
            }
          } finally {
            card.classList.remove('is-updating');
          }
        }
      });
    });
  }

  #generatePriceMarkup(price, msrp) {
    const p = parseFloat(price);
    const m = parseFloat(msrp || 0);
    const priceFormatted = formatMoney(p, this.currencySettings);
    let html = `<span class="price">${priceFormatted}</span>`;

    if (m > p) {
      const compareFormatted = formatMoney(m, this.currencySettings);
      const discountFormatted = formatMoney(m - p, this.currencySettings);
      html = `<span class="compare-at-price">${compareFormatted}</span> ${html} <span class="save-amount-wrapper">Save ${discountFormatted}</span>`;
    }
    return html;
  }

  #getCookie(name) {
    return document.cookie.split('; ').find(row => row.startsWith(`${name}=`))?.split('=')[1] || '';
  }
}

if (!customElements.get('cross-sell-component')) {
  customElements.define('cross-sell-component', CrossSellComponent);
}